// /pages/qrscan.js
import React, { useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom"; // v5
import { Html5Qrcode } from "html5-qrcode";
import { getUser } from "../utils/auth";
import { apiFetch } from "../utils/wkCommon";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const rem = (v) => `${v}rem`;

/**
 * QR Scan page
 * - switch camera if multiple available
 * - segmented toggle Peserta / Petugas (centered pill)
 * - fancy AutoScan toggle
 * - robust start/stop handling for html5-qrcode
 * - responsive layout
 *
 * Requires: yarn add html5-qrcode@2.3.8
 */

export default function QrScanPage() {
  const history = useHistory();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const eventId = params.get("eventId") || params.get("event.id") || null;

  const [selectedEvent, setSelectedEvent] = useState(null);

  const containerRef = useRef(null);
  const scannerRef = useRef(null);
  const runningRef = useRef(false);
  const lastScanRef = useRef(0);

  const [modePeserta, setModePeserta] = useState(true);
  const [autoScan, setAutoScan] = useState(false);
  const [message, setMessage] = useState(null);
  const [scanning, setScanning] = useState(false);

  const [cameras, setCameras] = useState([]); // list of camera devices
  const [cameraId, setCameraId] = useState(null); // selected camera id
  const [refreshKey, setRefreshKey] = useState(0); // force restart scanner

  useEffect(() => {
    return () => {
      stopScanner();
    };
  }, []);

  // flash message
  function flash(type, text, ms = 1200) {
    setMessage({ type, text });
    if (ms) setTimeout(() => setMessage(null), ms);
  }

  async function stopScanner() {
    const s = scannerRef.current;
    if (!s) return;

    try {
      await s.stop();
    } catch (e) {
      console.warn("stopScanner stop error", e);
    }

    try {
      await s.clear();
    } catch (e) {
      console.warn("stopScanner clear error", e);
    }

    scannerRef.current = null;
    setScanning(false);
  }

  async function handleScanResult(codeStr) {
    if (!codeStr) return;

    // throttle scan (max 1x per detik)
    const now = Date.now();
    if (now - lastScanRef.current < 1000) return;
    lastScanRef.current = now;

    // block kalau masih proses
    if (runningRef.current) return;

    runningRef.current = true;

    try {
      const umatDocId = codeStr.trim();
      const umatResp = await apiFetch(`/api/umats?filters[umatID][$eq]=${umatDocId}&fields[0]=id`);

      const umatData = umatResp?.data ?? umatResp;
      const umatId = umatData?.id || (umatData.length >= 1 ? umatData[0].id : null);
      if (!umatId) {
        flash("error", `Umat tidak ditemukan: ${umatDocId}`, 1200);
        runningRef.current = false;
        return;
      }
      const user = getUser() || {};
      const username = user?.username || "system";

      // cek existing participant (hindari duplikasi), exclude deleted
      // const checkUrl = `/api/event-participants?filters%5Bevent%5D%5Bid%5D%5B$eq%5D=${eventId}&filters%5Bumat%5D%5Bid%5D%5B$eq%5D=${umatId}`;
      const checkUrl = `/api/event-participants?` +
        `filters%5Bevent%5D%5Bid%5D%5B$eq%5D=${eventId}&` +
        `filters%5Bumat%5D%5Bid%5D%5B$eq%5D=${umatId}&` +
        `filters%5Bdeleted%5D%5B$null%5D=true`;
      const existingResp = await apiFetch(checkUrl);
      const existingData = existingResp?.data ?? existingResp;

      if (Array.isArray(existingData) ? existingData.length > 0 : !!existingData?.id) {
        flash("info", `Sudah terdaftar`, 900);
        runningRef.current = false;
        return;
      }

      const payload = { 
        data: {
          personType: "Internal",
          peserta: !!modePeserta,
          umat: umatId,
          event: eventId || null,
          recordOwner: username,
          recordEditor: username,
        },
      };

      await apiFetch(`/api/event-participants`, { method: "POST", body: payload });

      if (autoScan) {
        flash("success", `Added umat ${umatId}`, 700);

        // cooldown supaya tidak double scan / lompat ke redirect
        setTimeout(() => {
          runningRef.current = false;
        }, 800);

        return;
      } else {
        /*
        flash("success", `Berhasil — umat ${umatId}`, 1100);
        // inside success branch (single-scan)
        console.log(selectedEvent, selectedEvent?.agenda, selectedEvent?.agenda?.documentId)
        const targetAgendaId = selectedEvent?.agenda?.documentId;
        const target = targetAgendaId ? `/presensi/${targetAgendaId}` : `/presensi`;
        */
        flash("success", `Berhasil — umat ${umatId}`, 1100);

        // pastikan event sudah ada, kalau belum fetch ulang
        let agendaId = selectedEvent?.agenda?.documentId;

        if (!agendaId && eventId) {
          try {
            const resp = await apiFetch(`/api/events?filters[id][$eq]=${eventId}&populate[0]=agenda`);
            const data = resp?.data;
            const evt = Array.isArray(data) ? data[0] : data;

            const agendaObj = evt?.agenda?.data || evt?.agenda;
            agendaId = agendaObj?.documentId || null;
          } catch (e) {
            console.warn("refetch agenda failed", e);
          }
        }

        const target = agendaId ? `/presensi/${agendaId}` : `/presensi`;

        // beri delay kecil supaya user sempat lihat success
        setTimeout(() => {
          // double check sebelum redirect
          if (!autoScan) {
            history.replace(target);
          }
        }, 800);
        
        //setTimeout(() => history.replace(target), 1000);
      }
    } catch (err) {
      console.error("handleScanResult err", err);
      flash("error", `Gagal: ${err?.message ?? "server error"}`, 1600);
    } finally {
      runningRef.current = false;
    }
  }

  // safe thenable helper
  const handleMaybeThenable = (maybe, onResolved) => {
    if (!maybe) { try { onResolved && onResolved(); } catch (e) {} ; return; }
    if (typeof maybe.then === "function") {
      maybe.then(onResolved).catch((e) => { console.warn("ignored thenable error", e); onResolved && onResolved(); });
    } else {
      try { onResolved && onResolved(); } catch (e) {}
    }
  };

    useEffect(() => {
    if (!eventId) return;

    let mounted = true;
    async function loadEvent() {
        try {
        // populate agenda relation so we can read agenda.subject
        // Strapi v5 populate one relation: ?populate[0]=agenda
        const resp = await apiFetch(`/api/events?filters[id][$eq]=${eventId}&populate[0]=agenda`);
        // resp may be { data: { id, attributes: { ... } } } or direct depending on apiFetch wrapper
        // Normalize basic shapes:
        const data = resp?.data;
        const evt = data.length >= 1 ? data[0] : data;

        if (mounted) setSelectedEvent(evt);
        } catch (e) {
        console.error("loadEvent error", e);
        // ignore silently or show small flash if you prefer
        }
    }

    loadEvent();
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [eventId]);

  // Start / restart scanner when cameraId or refreshKey changes
  useEffect(() => {
    let html5QrCode = null;
    let isMounted = true;
    let started = false;

    async function startScanner() {
      if (started) return;
      if (!containerRef.current) {
        setTimeout(startScanner, 100);
        return;
      }
      started = true;

      try {
        const container = containerRef.current;
        const innerId = "html5qr-inside";
        const prev = container.querySelector(`#${innerId}`);
        if (prev) prev.remove();

        const el = document.createElement("div");
        el.id = innerId;
        el.style.width = "100%";
        el.style.height = "100%";
        el.style.position = "relative";
        el.style.overflow = "hidden";
        container.appendChild(el);

        // inject style to center/cover video & center overlay
        if (!document.getElementById("html5qrcode-custom-styles")) {
          const style = document.createElement("style");
          style.id = "html5qrcode-custom-styles";
          style.innerHTML = `
            #${innerId} video, #${innerId} canvas {
              width: 100% !important;
              height: 100% !important;
              object-fit: cover !important;
              object-position: center center !important;
              transform: none !important;
              left: 0 !important;
              top: 0 !important;
            }
            #${innerId} .qrbox {
              position: absolute !important;
              left: 50% !important;
              top: 50% !important;
              transform: translate(-50%, -50%) !important;
              box-sizing: border-box !important;
            }
          `;
          document.head.appendChild(style);
        }

        html5QrCode = new Html5Qrcode(el.id, false);
        scannerRef.current = html5QrCode;

        // get cameras list once (and update cameras state)
        const devices = await Html5Qrcode.getCameras().catch((e) => {
          console.warn("getCameras error", e);
          return [];
        });

        // filter kamera valid (hindari duplicate / unusable)
        const validCameras = (devices || []).filter((d) => {
          const label = (d.label || "").toLowerCase();

          // biasanya kamera aneh tidak ada label jelas
          if (!label) return false;

          // opsional: skip camera depth / ultra wide yg sering gagal
          if (label.includes("depth") || label.includes("infrared")) return false;

          return true;
        });

        if (isMounted) {
          setCameras(validCameras);

          if (!cameraId && validCameras.length) {
            // prioritas: back camera
            const backCam =
              validCameras.find((c) =>
                (c.label || "").toLowerCase().includes("back")
              ) || validCameras[0];

            setCameraId((prev) => prev || backCam.id);
          }
        }

        // choose cameraId param (from state or fallback)
        let chosen = cameraId;

        if (!chosen && devices && devices.length) {
          const backCam =
            devices.find((c) =>
              (c.label || "").toLowerCase().includes("back")
            ) || devices[0];

          chosen = backCam.id;
        }

        // compute qrbox size
        const maxDimension = Math.min(window.innerWidth, window.innerHeight);
        const qrboxSize = Math.max(220, Math.min(480, Math.floor(maxDimension * 0.55)));

        const config = {
          fps: 10,
          qrbox: { width: qrboxSize, height: qrboxSize },
          experimentalFeatures: { useBarCodeDetectorIfSupported: true },
        };

        await html5QrCode.start(
          chosen,
          config,
          (decodedText) => {
            if (!isMounted) return;
            handleScanResult(decodedText);
          },
          (err) => {
            /* ignore frame decode errors */
          }
        );

        if (isMounted) setScanning(true);
      } catch (e) {
        console.error("startScanner error", e);
        flash("error", "Kamera tidak tersedia / akses ditolak", 4000);

        try { if (html5QrCode) await html5QrCode.stop(); } catch(e){}
        try { if (html5QrCode) await html5QrCode.clear(); } catch(e){}

        const currentDevices = cameras || [];
        const currentId = cameraId;

        // fallback hanya 1x, hindari loop
        if (currentDevices.length > 1) {
          const alt = currentDevices.find((d) => d.id !== currentId);

          if (alt && alt.id !== currentId) {
            setCameraId(alt.id);

            // jangan spam restart
            setTimeout(() => {
              setRefreshKey((k) => k + 1);
            }, 300);

            return; // penting: stop di sini
          }
        }

        scannerRef.current = null;
        started = false;
      }
    }

    startScanner();

    return () => {
      isMounted = false;
      setScanning(false);
      const s = scannerRef.current;

      if (s) {
        let stopRes;
        try {
          stopRes = s.stop();
        } catch (e) {
          console.warn("s.stop threw (ignored)", e);
          stopRes = null;
        }
        handleMaybeThenable(stopRes, () => {
          let clearRes = null;
          try {
            clearRes = s.clear ? s.clear() : null;
          } catch (e) {
            console.warn("s.clear threw (ignored)", e);
            clearRes = null;
          }
          handleMaybeThenable(clearRes, () => {
            try {
              const container = containerRef.current;
              if (container) {
                const child = container.querySelector("#html5qr-inside");
                if (child && child.parentNode) child.parentNode.removeChild(child);
              }
            } catch (ee) {}
            scannerRef.current = null;
          });
        });
      } else {
        try {
          const container = containerRef.current;
          if (container) {
            const child = container.querySelector("#html5qr-inside");
            if (child && child.parentNode) child.parentNode.removeChild(child);
          }
        } catch (e) {}
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cameraId, refreshKey]); // restart when cameraId changed or refresh requested

  // refresh / restart camera
  const restartCamera = () => {
    setRefreshKey((k) => k + 1);
    flash("info", "Restarting camera...", 700);
  };

  // UI icons
  const IconRefresh = ({ size = rem(1.1) }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M21 12a9 9 0 10-3.17 6.37L21 12z" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M21 3v6h-6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
  const IconPerson = ({ size = rem(0.95) }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden><path d="M12 12a4 4 0 100-8 4 4 0 000 8zM4 20a8 8 0 0116 0" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>
  );
  const IconShield = ({ size = rem(0.95) }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden><path d="M12 2l7 3v5c0 5-3.5 9.8-7 11-3.5-1.2-7-6-7-11V5l7-3z" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg>
  );

  // styles
  const styles = {
    page: { padding: rem(1), fontSize: rem(1), maxWidth: 1100, margin: "0 auto" },
    header: { display: "flex", alignItems: "center", gap: rem(0.6), marginBottom: rem(0.6) },
    backBtn: { padding: `${rem(0.36)} ${rem(0.6)}`, borderRadius: rem(0.6), border: "1px solid #ddd", background: "#fff", cursor: "pointer" },
    controlsRow: { marginTop: rem(0.6), display: "flex", alignItems: "center", gap: rem(0.6), flexWrap: "wrap" },
    segmentedWrap: { position: "relative", display: "inline-flex", padding: rem(0.12), borderRadius: rem(1), background: "#f3f5f7", border: "1px solid rgba(0,0,0,0.06)" },
    segBtn: (active) => ({
      position: "relative",
      padding: `${rem(0.45)} ${rem(1)}`,
      borderRadius: rem(0.9),
      border: "none",
      background: "transparent",
      cursor: "pointer",
      display: "inline-flex",
      alignItems: "center",
      gap: rem(0.45),
      color: active ? "#072f6b" : "#444",
      fontWeight: active ? 600 : 500,
      zIndex: 2,
      fontSize: rem(0.95),
    }),
    segIndicator: (leftPercent) => ({
      position: "absolute",
      top: rem(0.12),
      left: `${leftPercent}%`,
      transform: "translateX(-50%)",
      width: "50%",
      height: `calc(100% - ${rem(0.24)})`,
      background: "#fff",
      borderRadius: rem(0.9),
      boxShadow: "0 6px 18px rgba(2, 45, 90, 0.06)",
      transition: "left 220ms cubic-bezier(.2,.9,.2,1)",
      zIndex: 1,
    }),
    autoToggleWrap: { marginLeft: "auto", display: "flex", alignItems: "center", gap: rem(0.6) },
    fancyToggle: (on) => ({
      width: rem(3.2),
      height: rem(1.8),
      borderRadius: rem(2),
      background: on ? "linear-gradient(90deg,#00c853,#00bfa5)" : "#2b2f36",
      display: "inline-flex",
      alignItems: "center",
      padding: rem(0.14),
      position: "relative",
      cursor: "pointer",
      transition: "background 180ms",
    }),
    fancyKnob: (on) => ({
      width: rem(1.4),
      height: rem(1.4),
      borderRadius: "50%",
      background: "#fff",
      transform: on ? `translateX(${rem(1.4)})` : "translateX(0)",
      transition: "transform 180ms",
      boxShadow: "0 4px 10px rgba(0,0,0,0.12)",
    }),
    previewWrap: { marginTop: rem(0.6), width: "100%", maxWidth: 980, margin: "0 auto", borderRadius: rem(0.6), overflow: "hidden", boxShadow: "0 6px 18px rgba(0,0,0,0.06)", minHeight: rem(22), background: "#000", display: "flex", alignItems: "center", justifyContent: "center" },
    previewContainer: { width: "80%", height: "80%", minHeight: rem(22), display: "flex", alignItems: "center", justifyContent: "center", color: "#fff" },
    rightControls: { display: "flex", gap: rem(0.5), alignItems: "center" },
    camSelect: { padding: `${rem(0.28)} ${rem(0.5)}`, borderRadius: rem(0.45), border: "1px solid #ddd", background: "#fff" },
    smallMuted: { fontSize: rem(0.85), color: "#666" },
    iconBtn: { display: "inline-flex", alignItems: "center", justifyContent: "center", padding: rem(0.35), borderRadius: rem(0.45), border: "1px solid rgba(0,0,0,0.06)", background: "#fff", cursor: "pointer" },
    leftControls: { display: "flex", alignItems: "center", gap: rem(0.6) },
    centerControls: { flex: 1, display: "flex", justifyContent: "center", alignItems: "center" },
    rightControls: { display: "flex", gap: rem(0.5), alignItems: "center" }, // keep existing name
    msg: (t) => ({ marginTop: rem(0.6), padding: rem(0.6), borderRadius: rem(0.45), background: t === "success" ? "#e8f5e9" : "#fff0f0", color: t === "success" ? "#1b5e20" : "#b71c1c" }),
  };

  // Back button behaviour
  const goBack = async () => {
    await stopScanner();

    const st = (location && location.state && location.state.returnTo) || null;
    if (st && typeof st === "string") history.push(st);
    else history.goBack();
  };

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <button style={styles.backBtn} onClick={goBack} aria-label="Back">
          ← Back
        </button>
        <div>
            {/* Title */}
            <div style={{ fontSize: rem(1.1), fontWeight: 600 }}>
                { selectedEvent?.agenda?.subject || "Scan QR" }
            </div>
        </div>
      </div>

      {message && <div style={styles.msg(message.type)}>{message.text}</div>}

      <div style={{ display: "flex", justifyContent: "space-between", marginTop: rem(0.6), marginBottom: rem(0.6), alignItems: "center" }}>
        <div style={styles.smallMuted}>Mode: <strong>{modePeserta ? "Peserta" : "Petugas"}</strong></div>
        <div style={styles.smallMuted}>Auto Scan: <strong>{autoScan ? "On" : "Off"}</strong></div>
      </div>

      <div style={styles.previewWrap}>
        <div ref={containerRef} style={styles.previewContainer}>
          {!scanning && <div style={{ padding: rem(1), textAlign: "center" }}>Menunggu kamera... (izinkan akses)</div>}
        </div>
      </div>
      <div style={styles.controlsRow}>
        {/* LEFT: segmented toggle (left aligned) */}
        <div style={styles.leftControls}>
          <div style={styles.segmentedWrap} role="tablist" aria-label="Mode peserta petugas">
            <div style={styles.segIndicator(modePeserta ? 25 : 75)} />
            <button
              onClick={() => setModePeserta(true)}
              style={styles.segBtn(modePeserta)}
              aria-pressed={modePeserta}
              title="Peserta"
            >
              <IconPerson />
              <span>Peserta</span>
            </button>
            <button
              onClick={() => setModePeserta(false)}
              style={styles.segBtn(!modePeserta)}
              aria-pressed={!modePeserta}
              title="Petugas"
            >
              <IconShield />
              <span>Petugas</span>
            </button>
          </div>
        </div>

        {/* CENTER: camera select + refresh (centered) */}
        <div style={styles.centerControls}>
          <div style={{ display: "flex", alignItems: "center", gap: rem(0.5) }}>
            {cameras && cameras.length > 0 && (
              <select
                value={cameraId || ""}
                onChange={(e) => setCameraId(e.target.value)}
                style={styles.camSelect}
                aria-label="Pilih kamera"
              >
                {cameras.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.label || c.id}
                  </option>
                ))}
              </select>
            )}

            <button title="Restart camera" onClick={restartCamera} style={styles.iconBtn} aria-label="Restart camera">
              <IconRefresh />
            </button>
          </div>
        </div>

        {/* RIGHT: AutoScan toggle (right aligned) */}
        <div style={{ marginLeft: rem(0.6) }}>
          <div style={styles.autoToggleWrap}>
            <div style={{ display: "flex", alignItems: "center", gap: rem(0.45) }}>
              <div style={{ fontSize: rem(0.95) }}>Auto Scan</div>
              <div
                role="switch"
                aria-checked={autoScan}
                onClick={() => setAutoScan((s) => !s)}
                style={styles.fancyToggle(autoScan)}
              >
                <div style={styles.fancyKnob(autoScan)} />
              </div>
            </div>
          </div>
        </div>
      </div>


      <div style={{ marginTop: rem(0.8), color: "#555", fontSize: rem(0.9), display:"none" }}>
        <div>Petunjuk:</div>
        <ul>
          <li>Pastikan kamera diizinkan di browser/perangkat.</li>
          <li>Pilih kamera (jika tersedia) atau tekan restart bila kamera bermasalah.</li>
          <li>QR harus berisi `umat` documentId (contoh: <code>123</code>); jika format berbeda, sesuaikan `handleScanResult`.</li>
        </ul>
      </div>
    </div>
  );
}
